// packages/shared/src/constants/chiroCatalogDefaults.ts
// Default flow sheet catalog items for Chiropractic.
// These serve as frontend defaults when no org-specific catalog exists.
// Organization ID 'system' marks these as built-in defaults.

import type { FlowSheetItemCatalogEntry } from '../types/models/flowSheet';

const ts = 0; // placeholder timestamps for defaults

/** Helper to build a catalog entry with common defaults */
function entry(
  id: string,
  label: string,
  data_type: FlowSheetItemCatalogEntry['data_type'],
  category: FlowSheetItemCatalogEntry['category'],
  opts: Partial<FlowSheetItemCatalogEntry> = {}
): FlowSheetItemCatalogEntry {
  return {
    id,
    organization_id: 'system',
    label,
    data_type,
    category,
    is_active: true,
    created_at: ts,
    updated_at: ts,
    ...opts,
  };
}

// ── ROM (category: 'rom') ───────────────────────────────────────────────

const ROM_ITEMS: FlowSheetItemCatalogEntry[] = [
  // Cervical
  entry('chiro_rom_cervical_flex', 'Cervical Flexion', 'numeric', 'rom', { unit: 'deg', bilateral: false, body_regions: ['cervical'] }),
  entry('chiro_rom_cervical_ext', 'Cervical Extension', 'numeric', 'rom', { unit: 'deg', bilateral: false, body_regions: ['cervical'] }),
  entry('chiro_rom_cervical_lat_flex', 'Cervical Lateral Flexion', 'numeric', 'rom', { unit: 'deg', bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_rom_cervical_rot', 'Cervical Rotation', 'numeric', 'rom', { unit: 'deg', bilateral: true, body_regions: ['cervical'] }),
  // Thoracic
  entry('chiro_rom_thoracic_rot', 'Thoracic Rotation', 'numeric', 'rom', { unit: 'deg', bilateral: true, body_regions: ['thoracic'] }),
  // Lumbar
  entry('chiro_rom_lumbar_flex', 'Lumbar Flexion', 'numeric', 'rom', { unit: 'deg', bilateral: false, body_regions: ['lumbar'] }),
  entry('chiro_rom_lumbar_ext', 'Lumbar Extension', 'numeric', 'rom', { unit: 'deg', bilateral: false, body_regions: ['lumbar'] }),
  entry('chiro_rom_lumbar_lat_flex', 'Lumbar Lateral Flexion', 'numeric', 'rom', { unit: 'deg', bilateral: true, body_regions: ['lumbar'] }),
  entry('chiro_rom_lumbar_rot', 'Lumbar Rotation', 'numeric', 'rom', { unit: 'deg', bilateral: true, body_regions: ['lumbar'] }),
];

// ── Palpation (category: 'palpation') — NEW chiro-specific category ────

const PALPATION_ITEMS: FlowSheetItemCatalogEntry[] = [
  // Muscle Spasm
  entry('palp_spasm_cervical', 'Muscle Spasm — Cervical', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['cervical'] }),
  entry('palp_spasm_thoracic', 'Muscle Spasm — Thoracic', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['thoracic'] }),
  entry('palp_spasm_lumbar', 'Muscle Spasm — Lumbar', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['lumbar'] }),
  // Tenderness
  entry('palp_tenderness_cervical', 'Tenderness — Cervical', 'scale', 'palpation', { scale_min: 0, scale_max: 4, body_regions: ['cervical'] }),
  entry('palp_tenderness_thoracic', 'Tenderness — Thoracic', 'scale', 'palpation', { scale_min: 0, scale_max: 4, body_regions: ['thoracic'] }),
  entry('palp_tenderness_lumbar', 'Tenderness — Lumbar', 'scale', 'palpation', { scale_min: 0, scale_max: 4, body_regions: ['lumbar'] }),
  entry('palp_tenderness_sacral', 'Tenderness — Sacral', 'scale', 'palpation', { scale_min: 0, scale_max: 4, body_regions: ['sacral'] }),
  // Fixation
  entry('palp_fixation_cervical', 'Fixation — Cervical', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['cervical'] }),
  entry('palp_fixation_thoracic', 'Fixation — Thoracic', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['thoracic'] }),
  entry('palp_fixation_lumbar', 'Fixation — Lumbar', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['lumbar'] }),
  entry('palp_fixation_sacral', 'Fixation — Sacral', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['sacral'] }),
  entry('palp_fixation_pelvic', 'Fixation — Pelvic', 'select', 'palpation', { select_options: ['None', 'Mild', 'Moderate', 'Severe'], body_regions: ['pelvic'] }),
];

// ── Pain (category: 'pain') — reuse IDs from PT catalog ────────────────

const PAIN_ITEMS: FlowSheetItemCatalogEntry[] = [
  entry('pain_current', 'Current Pain (NPRS)', 'scale', 'pain', { unit: '/10', bilateral: false, scale_min: 0, scale_max: 10 }),
  entry('pain_best', 'Best Pain (24h)', 'scale', 'pain', { unit: '/10', bilateral: false, scale_min: 0, scale_max: 10 }),
  entry('pain_worst', 'Worst Pain (24h)', 'scale', 'pain', { unit: '/10', bilateral: false, scale_min: 0, scale_max: 10 }),
];

// ── Outcome Measures (category: 'outcome_measure') ─────────────────────

const OUTCOME_ITEMS: FlowSheetItemCatalogEntry[] = [
  // Reuse existing IDs
  entry('outcome_ndi', 'NDI', 'numeric', 'outcome_measure', { unit: '%', body_regions: ['cervical'] }),
  entry('outcome_odi', 'Oswestry Disability Index', 'numeric', 'outcome_measure', { unit: '%', body_regions: ['lumbar'] }),
  entry('outcome_psfs', 'Patient-Specific Functional Scale', 'numeric', 'outcome_measure', { unit: '/10' }),
  // Chiro-specific
  entry('chiro_outcome_bournemouth_back', 'Bournemouth Back Questionnaire', 'numeric', 'outcome_measure', { unit: '/70', body_regions: ['lumbar'] }),
  entry('chiro_outcome_fabq_pa', 'FABQ — Physical Activity', 'numeric', 'outcome_measure', { unit: '/24' }),
  entry('chiro_outcome_fabq_work', 'FABQ — Work', 'numeric', 'outcome_measure', { unit: '/42' }),
  entry('chiro_outcome_roland_morris', 'Roland-Morris Disability', 'numeric', 'outcome_measure', { unit: '/24', body_regions: ['lumbar'] }),
];

// ── Special Tests (category: 'special_test') ───────────────────────────

const SPECIAL_TEST_ITEMS: FlowSheetItemCatalogEntry[] = [
  entry('chiro_st_spurling', "Spurling's Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_st_distraction', 'Cervical Distraction', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: false, body_regions: ['cervical'] }),
  entry('chiro_st_shoulder_dep', 'Shoulder Depression Test', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_st_valsalva', 'Valsalva Maneuver', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: false, body_regions: ['cervical', 'lumbar'] }),
  entry('chiro_st_jackson', "Jackson's Compression", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_st_slr', 'Straight Leg Raise', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['lumbar'] }),
  entry('chiro_st_kemp', "Kemp's Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['lumbar'] }),
  entry('chiro_st_yeoman', "Yeoman's Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['sacral', 'pelvic'] }),
  entry('chiro_st_gaenslen', "Gaenslen's Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['pelvic'] }),
  entry('chiro_st_faber', "Patrick's / FABER Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['hip', 'pelvic'] }),
  entry('chiro_st_hibb', "Hibb's Test", 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['pelvic'] }),
  entry('chiro_st_rib_spring', 'Rib Spring Test', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: true, body_regions: ['thoracic'] }),
  entry('chiro_st_adams', 'Adam\'s Test (Scoliosis)', 'select', 'special_test', { select_options: ['Positive', 'Negative', 'Equivocal', 'Not Tested'], bilateral: false, body_regions: ['thoracic', 'lumbar'] }),
];

// ── Neurological (category: 'special_test') ────────────────────────────

const NEURO_ITEMS: FlowSheetItemCatalogEntry[] = [
  entry('chiro_neuro_biceps_dtr', 'Biceps DTR (C5)', 'scale', 'special_test', { scale_min: 0, scale_max: 4, bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_neuro_brachiorad_dtr', 'Brachioradialis DTR (C6)', 'scale', 'special_test', { scale_min: 0, scale_max: 4, bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_neuro_triceps_dtr', 'Triceps DTR (C7)', 'scale', 'special_test', { scale_min: 0, scale_max: 4, bilateral: true, body_regions: ['cervical'] }),
  entry('chiro_neuro_patellar_dtr', 'Patellar DTR (L4)', 'scale', 'special_test', { scale_min: 0, scale_max: 4, bilateral: true, body_regions: ['lumbar'] }),
  entry('chiro_neuro_achilles_dtr', 'Achilles DTR (S1)', 'scale', 'special_test', { scale_min: 0, scale_max: 4, bilateral: true, body_regions: ['lumbar', 'sacral'] }),
  entry('chiro_neuro_babinski', 'Babinski Sign', 'select', 'special_test', { select_options: ['Positive', 'Negative'], bilateral: true }),
];

// ── Vital Signs (category: 'vital_signs') — reuse IDs from PT catalog ──

const VITAL_SIGNS_ITEMS: FlowSheetItemCatalogEntry[] = [
  entry('vs_hr', 'Heart Rate', 'numeric', 'vital_signs', { unit: 'bpm' }),
  entry('vs_bp_sys', 'Blood Pressure (Systolic)', 'numeric', 'vital_signs', { unit: 'mmHg' }),
  entry('vs_bp_dia', 'Blood Pressure (Diastolic)', 'numeric', 'vital_signs', { unit: 'mmHg' }),
  entry('vs_rr', 'Respiratory Rate', 'numeric', 'vital_signs', { unit: '/min' }),
  entry('vs_spo2', 'SpO2', 'numeric', 'vital_signs', { unit: '%' }),
];

// ── Full default catalog ─────────────────────────────────────────────────

export const CHIRO_CATALOG_DEFAULTS: FlowSheetItemCatalogEntry[] = [
  ...ROM_ITEMS,
  ...PALPATION_ITEMS,
  ...PAIN_ITEMS,
  ...OUTCOME_ITEMS,
  ...SPECIAL_TEST_ITEMS,
  ...NEURO_ITEMS,
  ...VITAL_SIGNS_ITEMS,
];

/** All default chiro catalog item IDs for convenience */
export const CHIRO_CATALOG_DEFAULT_IDS: string[] = CHIRO_CATALOG_DEFAULTS.map(item => item.id);
